/**
 * Canonical banner location codes for the resident app.
 *
 * Use `RESIDENT_BANNER_LOCATION_CODES` when creating banner locations in core.
 * The resident app consumes the compiled runtime keys derived from these codes.
 */
export const RESIDENT_BANNER_MODULE_KEYS = [
  'access-management',
  'billing',
  'cart',
  'checkout',
  'classifieds',
  'contact-groups',
  'documents',
  'eforms',
  'events',
  'facilities',
  'facilities-bookings',
  'faq',
  'gallery',
  'home',
  'iframe',
  'management',
  'map',
  'more',
  'newsstand',
  'orders',
  'panic',
  'pets',
  'products',
  'properties',
  'profile',
  'settings',
  'suppliers',
  'support',
  'surveys',
  'utilities',
  'vehicles',
] as const;

export type ResidentBannerModuleKey = (typeof RESIDENT_BANNER_MODULE_KEYS)[number];
export type ResidentBannerPosition = 'top' | 'bottom';

export interface ResidentBannerLocationDefinition {
  moduleKey: ResidentBannerModuleKey;
  position: ResidentBannerPosition;
  coreCode: string;
  runtimeKey: string;
}

type ResidentBannerLocationCodePair = Record<ResidentBannerPosition, Omit<ResidentBannerLocationDefinition, 'moduleKey' | 'position'>>;

export const RESIDENT_BANNER_LOCATION_CODE_MAP: Record<ResidentBannerModuleKey, ResidentBannerLocationCodePair> = {
  'access-management': {
    top: { coreCode: 'ACCESS_MANAGEMENT_TOP_BANNER', runtimeKey: 'access-management-top-banner' },
    bottom: { coreCode: 'ACCESS_MANAGEMENT_BOTTOM_BANNER', runtimeKey: 'access-management-bottom-banner' },
  },
  billing: {
    top: { coreCode: 'BILLING_TOP_BANNER', runtimeKey: 'billing-top-banner' },
    bottom: { coreCode: 'BILLING_BOTTOM_BANNER', runtimeKey: 'billing-bottom-banner' },
  },
  cart: {
    top: { coreCode: 'CART_TOP_BANNER', runtimeKey: 'cart-top-banner' },
    bottom: { coreCode: 'CART_BOTTOM_BANNER', runtimeKey: 'cart-bottom-banner' },
  },
  checkout: {
    top: { coreCode: 'CHECKOUT_TOP_BANNER', runtimeKey: 'checkout-top-banner' },
    bottom: { coreCode: 'CHECKOUT_BOTTOM_BANNER', runtimeKey: 'checkout-bottom-banner' },
  },
  classifieds: {
    top: { coreCode: 'CLASSIFIEDS_TOP_BANNER', runtimeKey: 'classifieds-top-banner' },
    bottom: { coreCode: 'CLASSIFIEDS_BOTTOM_BANNER', runtimeKey: 'classifieds-bottom-banner' },
  },
  'contact-groups': {
    top: { coreCode: 'CONTACT_GROUPS_TOP_BANNER', runtimeKey: 'contact-groups-top-banner' },
    bottom: { coreCode: 'CONTACT_GROUPS_BOTTOM_BANNER', runtimeKey: 'contact-groups-bottom-banner' },
  },
  documents: {
    top: { coreCode: 'DOCUMENTS_TOP_BANNER', runtimeKey: 'documents-top-banner' },
    bottom: { coreCode: 'DOCUMENTS_BOTTOM_BANNER', runtimeKey: 'documents-bottom-banner' },
  },
  eforms: {
    top: { coreCode: 'EFORMS_TOP_BANNER', runtimeKey: 'eforms-top-banner' },
    bottom: { coreCode: 'EFORMS_BOTTOM_BANNER', runtimeKey: 'eforms-bottom-banner' },
  },
  events: {
    top: { coreCode: 'EVENTS_TOP_BANNER', runtimeKey: 'events-top-banner' },
    bottom: { coreCode: 'EVENTS_BOTTOM_BANNER', runtimeKey: 'events-bottom-banner' },
  },
  facilities: {
    top: { coreCode: 'FACILITIES_TOP_BANNER', runtimeKey: 'facilities-top-banner' },
    bottom: { coreCode: 'FACILITIES_BOTTOM_BANNER', runtimeKey: 'facilities-bottom-banner' },
  },
  'facilities-bookings': {
    top: { coreCode: 'FACILITIES_BOOKINGS_TOP_BANNER', runtimeKey: 'facilities-bookings-top-banner' },
    bottom: { coreCode: 'FACILITIES_BOOKINGS_BOTTOM_BANNER', runtimeKey: 'facilities-bookings-bottom-banner' },
  },
  faq: {
    top: { coreCode: 'FAQ_TOP_BANNER', runtimeKey: 'faq-top-banner' },
    bottom: { coreCode: 'FAQ_BOTTOM_BANNER', runtimeKey: 'faq-bottom-banner' },
  },
  gallery: {
    top: { coreCode: 'GALLERY_TOP_BANNER', runtimeKey: 'gallery-top-banner' },
    bottom: { coreCode: 'GALLERY_BOTTOM_BANNER', runtimeKey: 'gallery-bottom-banner' },
  },
  home: {
    top: { coreCode: 'HOME_TOP_BANNER', runtimeKey: 'home-top-banner' },
    bottom: { coreCode: 'HOME_BOTTOM_BANNER', runtimeKey: 'home-bottom-banner' },
  },
  iframe: {
    top: { coreCode: 'IFRAME_TOP_BANNER', runtimeKey: 'iframe-top-banner' },
    bottom: { coreCode: 'IFRAME_BOTTOM_BANNER', runtimeKey: 'iframe-bottom-banner' },
  },
  management: {
    top: { coreCode: 'MANAGEMENT_TOP_BANNER', runtimeKey: 'management-top-banner' },
    bottom: { coreCode: 'MANAGEMENT_BOTTOM_BANNER', runtimeKey: 'management-bottom-banner' },
  },
  map: {
    top: { coreCode: 'MAP_TOP_BANNER', runtimeKey: 'map-top-banner' },
    bottom: { coreCode: 'MAP_BOTTOM_BANNER', runtimeKey: 'map-bottom-banner' },
  },
  more: {
    top: { coreCode: 'MORE_TOP_BANNER', runtimeKey: 'more-top-banner' },
    bottom: { coreCode: 'MORE_BOTTOM_BANNER', runtimeKey: 'more-bottom-banner' },
  },
  newsstand: {
    top: { coreCode: 'NEWSSTAND_TOP_BANNER', runtimeKey: 'newsstand-top-banner' },
    bottom: { coreCode: 'NEWSSTAND_BOTTOM_BANNER', runtimeKey: 'newsstand-bottom-banner' },
  },
  orders: {
    top: { coreCode: 'ORDERS_TOP_BANNER', runtimeKey: 'orders-top-banner' },
    bottom: { coreCode: 'ORDERS_BOTTOM_BANNER', runtimeKey: 'orders-bottom-banner' },
  },
  panic: {
    top: { coreCode: 'PANIC_TOP_BANNER', runtimeKey: 'panic-top-banner' },
    bottom: { coreCode: 'PANIC_BOTTOM_BANNER', runtimeKey: 'panic-bottom-banner' },
  },
  pets: {
    top: { coreCode: 'PETS_TOP_BANNER', runtimeKey: 'pets-top-banner' },
    bottom: { coreCode: 'PETS_BOTTOM_BANNER', runtimeKey: 'pets-bottom-banner' },
  },
  products: {
    top: { coreCode: 'PRODUCTS_TOP_BANNER', runtimeKey: 'products-top-banner' },
    bottom: { coreCode: 'PRODUCTS_BOTTOM_BANNER', runtimeKey: 'products-bottom-banner' },
  },
  properties: {
    top: { coreCode: 'PROPERTIES_TOP_BANNER', runtimeKey: 'properties-top-banner' },
    bottom: { coreCode: 'PROPERTIES_BOTTOM_BANNER', runtimeKey: 'properties-bottom-banner' },
  },
  profile: {
    top: { coreCode: 'PROFILE_TOP_BANNER', runtimeKey: 'profile-top-banner' },
    bottom: { coreCode: 'PROFILE_BOTTOM_BANNER', runtimeKey: 'profile-bottom-banner' },
  },
  settings: {
    top: { coreCode: 'SETTINGS_TOP_BANNER', runtimeKey: 'settings-top-banner' },
    bottom: { coreCode: 'SETTINGS_BOTTOM_BANNER', runtimeKey: 'settings-bottom-banner' },
  },
  suppliers: {
    top: { coreCode: 'SUPPLIERS_TOP_BANNER', runtimeKey: 'suppliers-top-banner' },
    bottom: { coreCode: 'SUPPLIERS_BOTTOM_BANNER', runtimeKey: 'suppliers-bottom-banner' },
  },
  support: {
    top: { coreCode: 'SUPPORT_TOP_BANNER', runtimeKey: 'support-top-banner' },
    bottom: { coreCode: 'SUPPORT_BOTTOM_BANNER', runtimeKey: 'support-bottom-banner' },
  },
  surveys: {
    top: { coreCode: 'SURVEYS_TOP_BANNER', runtimeKey: 'surveys-top-banner' },
    bottom: { coreCode: 'SURVEYS_BOTTOM_BANNER', runtimeKey: 'surveys-bottom-banner' },
  },
  utilities: {
    top: { coreCode: 'UTILITIES_TOP_BANNER', runtimeKey: 'utilities-top-banner' },
    bottom: { coreCode: 'UTILITIES_BOTTOM_BANNER', runtimeKey: 'utilities-bottom-banner' },
  },
  vehicles: {
    top: { coreCode: 'VEHICLES_TOP_BANNER', runtimeKey: 'vehicles-top-banner' },
    bottom: { coreCode: 'VEHICLES_BOTTOM_BANNER', runtimeKey: 'vehicles-bottom-banner' },
  },
};

export function toResidentBannerLocationCode(
  moduleKey: ResidentBannerModuleKey,
  position: ResidentBannerPosition
): string {
  // Defensive: an unmapped moduleKey (e.g. a new route added without registering a
  // banner location) must not throw and crash the whole route render — derive a
  // stable code that simply won't match any configured banner.
  return (
    RESIDENT_BANNER_LOCATION_CODE_MAP[moduleKey]?.[position]?.coreCode
    ?? `${String(moduleKey).toUpperCase().replace(/-/g, '_')}_${position.toUpperCase()}_BANNER`
  );
}

export function toResidentBannerRuntimeKey(
  moduleKey: ResidentBannerModuleKey,
  position: ResidentBannerPosition
): string {
  // Defensive: see toResidentBannerLocationCode — fall back instead of throwing.
  return (
    RESIDENT_BANNER_LOCATION_CODE_MAP[moduleKey]?.[position]?.runtimeKey
    ?? `${String(moduleKey)}-${position}-banner`
  );
}

export const RESIDENT_BANNER_LOCATION_DEFINITIONS: ResidentBannerLocationDefinition[] =
  RESIDENT_BANNER_MODULE_KEYS.flatMap((moduleKey) => ([
    {
      moduleKey,
      position: 'top' as const,
      ...RESIDENT_BANNER_LOCATION_CODE_MAP[moduleKey].top,
    },
    {
      moduleKey,
      position: 'bottom' as const,
      ...RESIDENT_BANNER_LOCATION_CODE_MAP[moduleKey].bottom,
    },
  ]));

export const RESIDENT_BANNER_LOCATION_CODES = RESIDENT_BANNER_LOCATION_DEFINITIONS.map(
  (item) => item.coreCode
);

export const RESIDENT_BANNER_RUNTIME_KEYS = RESIDENT_BANNER_LOCATION_DEFINITIONS.map(
  (item) => item.runtimeKey
);
